import { useState } from "react";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { ChevronLeft, ChevronRight, Check, MessageCircle } from "lucide-react";
import { ROOMS, HOTEL } from "../data/site";

function RoomSlider({ images, alt }) {
  const [i, setI] = useState(0);
  const next = (e) => {
    e.stopPropagation();
    setI((p) => (p + 1) % images.length);
  };
  const prev = (e) => {
    e.stopPropagation();
    setI((p) => (p - 1 + images.length) % images.length);
  };

  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted">
      {images.map((src, idx) => (
        <img
          key={src}
          src={src}
          alt={`${alt} photo ${idx + 1}`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

      <button
        onClick={prev}
        aria-label="Previous image"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full grid place-items-center bg-white/85 hover:bg-white text-[#1A1515] shadow"
      >
        <ChevronLeft size={16} />
      </button>
      <button
        onClick={next}
        aria-label="Next image"
        className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full grid place-items-center bg-white/85 hover:bg-white text-[#1A1515] shadow"
      >
        <ChevronRight size={16} />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((src, idx) => (
          <button
            key={src}
            onClick={(e) => {
              e.stopPropagation();
              setI(idx);
            }}
            aria-label={`Go to image ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              idx === i ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Rooms() {
  return (
    <section
      id="stay"
      data-testid="rooms-section"
      className="section-padding bg-beige/40 dark:bg-card relative overflow-hidden"
    >
      <div aria-hidden className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-[#7A1A24]/10 blur-3xl" />

      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="eyebrow">Guest Accommodation · Secondary Service</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
            On-site rooms for<br />
            <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">your wedding guests.</span>
          </h2>
          <p className="mt-5 text-muted-foreground">
            Hosting a wedding? Out-of-town relatives stay just steps from the mandap.
            We also welcome travellers, tourists and event guests outside of any function.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {ROOMS.map((room, idx) => (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: idx * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`room-card-${room.id}`}
            >
              <Tilt
                tiltMaxAngleX={6}
                tiltMaxAngleY={6}
                glareEnable
                glareMaxOpacity={0.12}
                glareColor="#D4AF37"
                glarePosition="all"
                scale={1.01}
                transitionSpeed={1400}
                className="h-full"
              >
                <article className="h-full rounded-3xl border border-border bg-card p-4 shadow-xl shadow-black/[0.04] dark:shadow-black/30 flex flex-col">
                  <RoomSlider images={room.images} alt={room.name} />

                  <div className="flex items-center justify-between mt-5 px-1">
                    <h3 className="font-display text-2xl text-foreground">{room.name}</h3>
                    <span className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#A8862B] dark:text-[#E6C555]">
                      {room.badge}
                    </span>
                  </div>

                  <p className="mt-3 px-1 text-sm text-muted-foreground leading-relaxed">
                    {room.description}
                  </p>

                  <ul className="mt-5 px-1 grid grid-cols-2 gap-y-2 gap-x-3 text-sm text-foreground/85">
                    {room.features.map((f) => (
                      <li key={f} className="flex items-center gap-1.5">
                        <Check size={14} className="text-[#D4AF37] shrink-0" />
                        <span className="text-xs">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex items-center justify-between px-1">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                        Starting From
                      </div>
                      <div className="font-display text-lg text-foreground">
                        Contact for Pricing
                      </div>
                    </div>
                    <a
                      href={HOTEL.whatsappStay}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`room-book-${room.id}`}
                      className="btn-primary !px-5 !py-2.5 text-xs"
                    >
                      <MessageCircle size={14} />
                      Book Stay
                    </a>
                  </div>
                </article>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
